import { useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'

import { useEffect, useUnsafeEffect } from 'modules/core/hooks/useEffect'

import { useApprovalFlowForm } from 'modules/approvals/hooks/useApprovalFlowForm'
import type { ApprovalFlowArgs, ApproverArgs } from 'modules/approvals/lib/approvalFlows'

import AddApproverModal from './AddApproverModal'
import { AddStepButton } from './AddStepButton'
import { ApprovalStep } from './ApprovalStep'
import { Wrapper } from './Wrapper'

type StepArgs = ApprovalFlowArgs['steps'][number]

interface Props {
  approvalFlow: ApprovalFlowArgs | null
  showAddModal?: boolean
  canEdit: boolean
  canAddSteps: boolean
  canDeleteSteps: boolean
  showBorder?: boolean
  onChange: (approvalFlow: ApprovalFlowArgs) => void
  onCloseModal?: () => void
}

export const ApprovalFlowForm = ({
  approvalFlow,
  showAddModal,
  canEdit,
  canAddSteps,
  canDeleteSteps,
  showBorder,
  onChange,
  onCloseModal,
}: Props) => {
  const {
    approvalFlowData,
    addApproverToStep,
    deleteStep,
    addStep,
    updateStep,
    removeApproverFromStep,
  } = useApprovalFlowForm(approvalFlow ?? null)

  const [showAddApprover, setshowAddApprover] = useState(showAddModal)
  const [selectedStep, setSelectedStep] = useState<StepArgs | null>(null)

  const openModalToAddApprover = (step: StepArgs | null) => {
    setshowAddApprover(true)
    setSelectedStep(step)
  }

  const closeModalToAddApprover = () => {
    setshowAddApprover(false)
    setSelectedStep(null)
    if (onCloseModal) {
      onCloseModal()
    }
  }

  const addApprover = (step: StepArgs | null, approver: ApproverArgs) => {
    if (step) {
      return addApproverToStep(step.position, approver)
    }
    return addStep(approver)
  }

  useEffect(() => {
    setshowAddApprover(showAddModal)
  }, [showAddModal])

  useUnsafeEffect(() => {
    onChange(approvalFlowData)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [approvalFlowData])

  return (
    <>
      <Box gap={showBorder ? 's16' : 's0'}>
        <Wrapper showBorder={showBorder}>
          {approvalFlowData.steps.map((step, index) => {
            // Show delete button if there are more than one step, when there is only one step it can't be deleted.
            const showDeleteButton = canDeleteSteps && approvalFlowData.steps.length > 1

            return (
              <ApprovalStep
                onAddApprover={(stepToAdd) => openModalToAddApprover(stepToAdd)}
                onUpdateStep={(updatedStep) => updateStep(updatedStep)}
                onDeleteStep={deleteStep}
                removeApproverFromStep={removeApproverFromStep}
                key={index}
                step={step}
                index={index}
                canEdit={canEdit}
                canDeleteSteps={showDeleteButton}
              />
            )
          })}
        </Wrapper>
        {canAddSteps && <AddStepButton addStep={() => openModalToAddApprover(null)} />}
      </Box>
      {showAddApprover && (
        <AddApproverModal
          onClose={() => closeModalToAddApprover()}
          addApprover={(approver) => addApprover(selectedStep, approver)}
        />
      )}
    </>
  )
}
